import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Loader2, Search, Pencil, Trash2, Plus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface SavedAssignee {
  id: string;
  assignee_name: string;
  email: string | null;
  role: string | null;
  usage_count: number;
}

interface AssigneeManagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unaId?: string;
}

export const AssigneeManagementModal = ({
  open,
  onOpenChange,
  unaId,
}: AssigneeManagementModalProps) => {
  const { user } = useAuth();
  const [assignees, setAssignees] = useState<SavedAssignee[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState<SavedAssignee | null>(null);
  const [newAssignee, setNewAssignee] = useState({ name: "", email: "", role: "" });

  useEffect(() => {
    if (open && user) {
      fetchAssignees();
    }
  }, [open, user, unaId]);

  const fetchAssignees = async () => {
    setLoading(true);
    try {
      const query = supabase
        .from("saved_assignees")
        .select("*")
        .eq("user_id", user!.id)
        .order("usage_count", { ascending: false });

      if (unaId) {
        query.eq("una_id", unaId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setAssignees(data || []);
    } catch (error) {
      console.error("Error fetching assignees:", error);
      toast.error("Failed to load assignees");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAssignee = async () => {
    const trimmedName = newAssignee.name.trim();
    if (!trimmedName) {
      toast.error("Name is required");
      return;
    }

    if (trimmedName.length > 100) {
      toast.error("Name must be less than 100 characters");
      return;
    }

    // Validate email is provided and valid
    const trimmedEmail = newAssignee.email.trim();
    if (!trimmedEmail) {
      toast.error("Email address is required to send task notifications");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      const { error } = await supabase.from("saved_assignees").insert({
        user_id: user!.id,
        una_id: unaId || null,
        assignee_name: trimmedName,
        email: trimmedEmail,
        role: newAssignee.role.trim() || null,
        usage_count: 0,
      });

      if (error) {
        if (error.code === "23505") {
          toast.error("Assignee already exists");
        } else {
          throw error;
        }
        return;
      }

      toast.success("Assignee added successfully");
      setShowAddDialog(false);
      setNewAssignee({ name: "", email: "", role: "" });
      fetchAssignees();
    } catch (error) {
      console.error("Error adding assignee:", error);
      toast.error("Failed to add assignee");
    }
  };

  const handleUpdateAssignee = async () => {
    if (!selectedAssignee) return;

    const trimmedName = newAssignee.name.trim();
    if (!trimmedName) {
      toast.error("Name is required");
      return;
    }

    try {
      const { error } = await supabase
        .from("saved_assignees")
        .update({
          assignee_name: trimmedName,
          email: newAssignee.email.trim() || null,
          role: newAssignee.role.trim() || null,
        })
        .eq("id", selectedAssignee.id);

      if (error) throw error;

      toast.success("Assignee updated successfully");
      setShowEditDialog(false);
      setSelectedAssignee(null);
      setNewAssignee({ name: "", email: "", role: "" });
      fetchAssignees();
    } catch (error) {
      console.error("Error updating assignee:", error);
      toast.error("Failed to update assignee");
    }
  };

  const handleDeleteAssignee = async () => {
    if (!selectedAssignee) return;

    try {
      const { error } = await supabase
        .from("saved_assignees")
        .delete()
        .eq("id", selectedAssignee.id);

      if (error) throw error;

      toast.success("Assignee deleted successfully");
      setShowDeleteDialog(false);
      setSelectedAssignee(null);
      fetchAssignees();
    } catch (error) {
      console.error("Error deleting assignee:", error);
      toast.error("Failed to delete assignee");
    }
  };

  const filteredAssignees = assignees.filter(
    (assignee) =>
      assignee.assignee_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      assignee.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      assignee.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-orange-500",
      "bg-pink-500",
      "bg-teal-500",
    ];
    const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>👤 Manage Saved Assignees</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Your saved assignees for quick selection
            </p>
          </DialogHeader>

          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search assignees..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Assignee
              </Button>
            </div>

            <div className="flex-1 overflow-auto border rounded-md">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : filteredAssignees.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                  <p>No saved assignees yet</p>
                  <p className="text-sm">
                    Add team members for quick assignment on tasks
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredAssignees.map((assignee) => (
                    <div
                      key={assignee.id}
                      className="flex items-center justify-between p-3 hover:bg-accent/50"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className={getAvatarColor(assignee.assignee_name)}>
                            {getInitials(assignee.assignee_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{assignee.assignee_name}</div>
                          {assignee.email ? (
                            <div className="text-xs text-muted-foreground">
                              📧 {assignee.email}
                            </div>
                          ) : (
                            <div className="text-xs text-red-500">
                              ⚠️ No email - notifications disabled
                            </div>
                          )}
                          {assignee.role && (
                            <div className="text-xs text-muted-foreground">
                              {assignee.role}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground">
                          {assignee.usage_count} tasks
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedAssignee(assignee);
                              setNewAssignee({
                                name: assignee.assignee_name,
                                email: assignee.email || "",
                                role: assignee.role || "",
                              });
                              setShowEditDialog(true);
                            }}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedAssignee(assignee);
                              setShowDeleteDialog(true);
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="text-sm text-muted-foreground">
              {filteredAssignees.length} saved assignee
              {filteredAssignees.length !== 1 ? "s" : ""}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Assignee Dialog */}
      <Dialog
        open={showAddDialog || showEditDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowAddDialog(false);
            setShowEditDialog(false);
            setNewAssignee({ name: "", email: "", role: "" });
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {showEditDialog ? "Edit Assignee" : "Add New Assignee"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="assigneeName">Full Name *</Label>
              <Input
                id="assigneeName"
                value={newAssignee.name}
                onChange={(e) => setNewAssignee({ ...newAssignee, name: e.target.value })}
                placeholder="John Doe"
                maxLength={100}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="assigneeEmail">Email *</Label>
              <Input
                id="assigneeEmail"
                type="email"
                value={newAssignee.email}
                onChange={(e) => setNewAssignee({ ...newAssignee, email: e.target.value })}
                placeholder="john@example.com"
                required
              />
              <p className="text-xs text-muted-foreground">
                Required to send task assignment notifications
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="assigneeRole">Role</Label>
              <Input
                id="assigneeRole"
                value={newAssignee.role}
                onChange={(e) => setNewAssignee({ ...newAssignee, role: e.target.value })}
                placeholder="e.g., Board Member, Advisor"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddDialog(false);
                setShowEditDialog(false);
                setNewAssignee({ name: "", email: "", role: "" });
              }}
            >
              Cancel
            </Button>
            <Button onClick={showEditDialog ? handleUpdateAssignee : handleAddAssignee}>
              {showEditDialog ? "Update Assignee" : "Add Assignee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>⚠️ Delete Assignee?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedAssignee?.assignee_name}"?
              <br />
              <br />
              This assignee is used in {selectedAssignee?.usage_count} task
              {selectedAssignee?.usage_count !== 1 ? "s" : ""}. Tasks will keep the assignee,
              but they won't appear in your saved list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAssignee}>
              Delete Assignee
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
